import { Alert, Badge, Button, Col, Divider, Drawer, Row, Typography, message } from 'antd';
import axios from 'axios';
import { BUSINESS_URL, NOTIFICATION_URL } from 'constants/ApiConstants';
import React, { useEffect, useState } from 'react';
import Loader from 'components/UtilComponents/Loader';
import ModalCloseIcon from 'components/UtilComponents/ModalCloseIcon';
import EmptyContent from 'components/UtilComponents/EmptyContent';
import IconEmpty from 'assets/images/empty.png';
import TimeAgo from 'timeago-react';
import { useNavigate } from 'react-router-dom';
import { unionBy } from 'lodash';
import { getBreakPoint } from 'utils/layout';

const NotificationsPanel = ({ visible, close }) => {
  const isMobile = !getBreakPoint().includes('sm');
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [refreshing, setRefreshing] = useState(true);
  const limit = 10;
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [businessId, setBusinessId] = useState(null);

  useEffect(() => {
    if (visible) {
      onLoadNotifications(page);
    }
  }, [visible]);

  const onCloseDrawer = () => {
    close();
  };

  const onLoadNotifications = async (page) => {
    setRefreshing(true);
    try {
      const businessRes = await axios.get(`${BUSINESS_URL}/me?populate=true`);
      const res = await axios.get(`${NOTIFICATION_URL}/me?page=${page}&limit=${limit}`, {
        method: 'get',
        headers: {
          'X-API-Filters': JSON.stringify({
            channelType: ['FCM'], // only fetch push notifications
          }),
        },
      });

      if (res?.data.status === 'error') {
        message.error(res?.data.error);
      } else {
        const newNotifications = unionBy(notifications, res?.data?.notifications, '_id');
        setBusinessId(businessRes.data.business._id);
        setNotifications(newNotifications);
        setCount(res?.data?.count);
      }
    } catch (err) {
      Alert.alertWithType('error', 'Error', err.message);
    }
    setRefreshing(false);
  };

  const onLoadMore = () => {
    if (notifications.length < count) {
      setPage(page + 1);
      onLoadNotifications(page + 1);
    }
  };

  const onReadNotification = async (notificationId) => {
    try {
      const response = await axios.post(`${NOTIFICATION_URL}/${notificationId}/read`);

      if (response.data.status === 'success') {
        console.log('Notification marked as read');
      } else {
        console.error('Error updating notification status:', response.data.error);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const onActionNavigate = async (notification) => {
    /**** Mark notification as read only when it is unread ****/
    if (!notification?.isNotificationRead) {
      await onReadNotification(notification?._id);
    }

    const payload = notification?.metadata?.payload;
    const templateId = notification?.metadata?.templateId;
    let paths;
    if (templateId === 'dispatchCreated' || templateId === 'dispatchTransitUpdated' || templateId === 'dispatchCompleted') {
      if (payload?.buyer_id === businessId) {
        paths = {
          dispatchCreated: payload?.dispatch_id ? `/buy/purchaseorders/dispatch/${payload?.dispatch_id}` : '/dispatches/ongoing-dispatches',
          dispatchTransitUpdated: payload?.dispatch_id ? `/buy/purchaseorders/dispatch/${payload?.dispatch_id}` : '/dispatches/ongoing-dispatches',
          dispatchCompleted: payload?.dispatch_id ? `/buy/purchaseorders/dispatch/${payload?.dispatch_id}` : '/dispatches/completed-dispatches',
        };
      } else if (payload?.seller_id === businessId) {
        paths = {
          dispatchCreated: payload?.dispatch_id ? `/sell/orders/dispatch/${payload?.dispatch_id}` : '/dispatches/ongoing-dispatches',
          dispatchTransitUpdated: payload?.dispatch_id ? `/sell/orders/dispatch/${payload?.dispatch_id}` : '/dispatches/ongoing-dispatches',
          dispatchCompleted: payload?.dispatch_id ? `/sell/orders/dispatch/${payload?.dispatch_id}` : '/dispatches/completed-dispatches',
        };
      }
    } else {
      paths = {
        newRfq: '/sell/open-rfqs',
        offerReceived: payload?.link_to_listing ?? '/sell/my-listings',
        quoteAccepted: payload?.link_to_rfq ?? '/sell/quotes/accepted-quotes',
        quoteRejected: payload?.link_to_rfq ?? '/sell/quotes/rejected-quotes',
        orderConfirmed: payload.order_id ? `/sell/orders/active-orders/${payload.order_id}` : '/sell/orders/active-orders',
        orderPartiallyFulfilled: payload.order_id ? `sell/orders/active-orders/${payload.order_id}` : '/sell/orders/active-orders',
        orderCompleted: payload.order_id ? `/sell/orders/completed-orders/${payload.order_id}` : '/sell/orders/completed-orders',
        paymentIssued: '/payments',
        newOpenListing: '/buy/listings',
        quoteReceived: payload?.link_to_rfq ?? '/buy/rfqs/open-rfqs',
        offerRejected: payload?.link_to_listing ?? '/buy/offers/rejected-offers',
        offerAccepted: payload?.link_to_listing ?? '/buy/offers/accepted-offers',
        purchaseOrderCreated: payload.purchaserorder_id ? `buy/purchaseorders/created-orders/${payload.purchaserorder_id}` : '/buy/purchaseorders/active-orders',
        purchaseOrderPartiallyFulfilled: payload.purchaserorder_id ? `buy/purchaseorders/created-orders/${payload.purchaserorder_id}` : '/buy/purchaseorders/active-orders',
        purchaseOrderCompleted: payload.purchaserorder_id ? `buy/purchaseorders/completed-orders/${payload.purchaserorder_id}` : 'buy/purchaseorders/completed-orders',
        sustainabilityReport: '/sustainability-report',
        resetPassword: '/my-account',
      };
    }
    if (templateId === 'newOpenListing' || templateId === 'sustainabilityReport' || templateId === 'resetPassword' || templateId === 'newRfq' || templateId === 'paymentIssued') {
      navigate(paths[templateId]);
    } else {
      window.open(paths[templateId], '_self');
    }
  };

  return (
    <Drawer width={isMobile ? '90%' : '35%'} headerStyle={{ margin: '0px', height: '0px', padding: '0px' }} closeIcon={false} onClose={onCloseDrawer} open={visible}>
      <Row align="middle" justify="space-between">
        <Col>
          <Typography.Title level={5}>Notifications</Typography.Title>
          {notifications?.length > 0 && (
            <Typography.Paragraph type="secondary" className="text-small">
              Showing {notifications?.length} / {count} notifications
            </Typography.Paragraph>
          )}
        </Col>
        <Col>
          <Button className="mb-3" type="text" onClick={onCloseDrawer}>
            <ModalCloseIcon />
          </Button>
        </Col>
      </Row>

      <Divider className="my-2" />

      {refreshing && page === 1 ? (
        <Loader />
      ) : notifications?.length <= 0 ? (
        <EmptyContent source={IconEmpty} title={'No Notifications to be Shown'} />
      ) : (
        <>
          {notifications?.map((notification, i) => {
            const title = notification?.metadata?.options?.FCM?.title;
            const body = notification?.metadata?.options?.FCM?.body;
            const createdAt = notification?.createdAt;
            const bulletColor = () => {
              switch (notification?.metadata?.templateId) {
                case 'newOpenListing':
                case 'dispatchCreated':
                case 'purchaseOrderCreated':
                case 'newRfq':
                  return '#1A75DA'; //Primary
                case 'quoteRejected':
                case 'offerRejected':
                  return '#FC4922'; //Red
                case 'paymentIssued':
                case 'dispatchTransitUpdated':
                case 'purchaseOrderPartiallyFulfilled':
                case 'orderPartiallyFulfilled':
                  return '#EAC700'; //Yellow
                default:
                  return '#168f51'; //Green
              }
            };
            return (
              <>
                {i !== 0 ? <Divider className="my-0" /> : ''}
                <Row
                  align="middle"
                  wrap={false}
                  className="py-3 cursor-pointer"
                  style={{ backgroundColor: notification?.isNotificationRead ? 'white' : '#e8f1fb' }}
                  onClick={() => onActionNavigate(notification)}>
                  <Col span={1}>
                    <Badge color={bulletColor()} />
                  </Col>
                  <Col>
                    <Row wrap={false} justify="space-between">
                      <Col span={18}>
                        <Typography.Text strong>{title}</Typography.Text>
                      </Col>
                      <Col span={6}>
                        <Typography.Text type="secondary" ellipsis={true} className="float-right">
                          <TimeAgo datetime={createdAt} locale="en_US" />
                        </Typography.Text>
                      </Col>
                    </Row>
                    <Typography.Text>{body}</Typography.Text>
                  </Col>
                </Row>
              </>
            );
          })}
          {notifications?.length > 0 && notifications?.length < count && !refreshing && (
            <div className="d-flex justify-content-center">
              <Button onClick={onLoadMore}>Load More</Button>
            </div>
          )}
          {refreshing && <Loader />}
        </>
      )}
    </Drawer>
  );
};

export default NotificationsPanel;
